import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  MenuItem,
  Card,
  CardContent,
  CardActions,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import { apiRequest } from '../utils/api';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';

interface ProductType {
  id: number;
  productType: string;
  description: string | null;
  sellingPrice: number | null;
  unit: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

interface CostHistoryItem {
  id: number;
  productTypeId: number;
  userId: number | null;
  userName: string;
  sellingPrice: number | null;
  reason: string | null;
  createdAt: string | null;
}

export default function ProductTypePage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [items, setItems] = useState<ProductType[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const [openDialog, setOpenDialog] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [form, setForm] = useState({
    productType: '',
    description: '',
    sellingPrice: '',
    unit: 'Sq ft',
  });

  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
  const [historyItems, setHistoryItems] = useState<CostHistoryItem[]>([]);
  const [historyForName, setHistoryForName] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ProductType | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const showError = (message: string) =>
    setSnackbar({ open: true, message, severity: 'error' });
  const showSuccess = (message: string) =>
    setSnackbar({ open: true, message, severity: 'success' });

  const loadItems = async () => {
    setLoading(true);
    try {
      const response = await apiRequest('/producttype', { method: 'GET' });
      const data = (response.data || []) as ProductType[];
      setItems(
        data.map((d) => ({
          ...d,
          sellingPrice:
            d.sellingPrice != null && !Number.isNaN(Number(d.sellingPrice))
              ? Number(d.sellingPrice)
              : null,
        }))
      );
    } catch (error) {
      console.error('Error loading product types:', error);
      showError(
        error instanceof Error ? error.message : 'Failed to load product types'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const handleOpenCreate = () => {
    setIsEdit(false);
    setSelectedId(null);
    setForm({
      productType: '',
      description: '',
      sellingPrice: '',
      unit: 'Sq ft',
    });
    setOpenDialog(true);
  };

  const handleOpenEditCost = (item: ProductType) => {
    setIsEdit(true);
    setSelectedId(item.id);
    setForm({
      productType: item.productType,
      description: item.description || '',
      sellingPrice:
        item.sellingPrice != null ? item.sellingPrice.toString() : '',
      unit: item.unit || 'Sq ft',
    });
    setOpenDialog(true);
  };

  const handleSave = async () => {
    if (!form.productType || !form.sellingPrice) {
      showError('Product Type and Selling Price are required');
      return;
    }

    const numericSellingPrice = Number(form.sellingPrice);

    if (Number.isNaN(numericSellingPrice)) {
      showError('Selling Price must be a valid number');
      return;
    }

    setSaving(true);
    try {
      if (!isEdit) {
        // Create new product type
        const response = await apiRequest('/producttype', {
          method: 'PUT',
          body: JSON.stringify({
            productType: form.productType,
            description: form.description || null,
            sellingPrice: numericSellingPrice,
            unit: form.unit || null,
          }),
        });
        const data = response.data as { productType: ProductType };
        setItems((prev) => [data.productType, ...prev]);
        showSuccess('Product type created');
      } else if (selectedId != null) {
        // Update prices
        const response = await apiRequest(`/producttype/${selectedId}`, {
          method: 'POST',
          body: JSON.stringify({
            sellingPrice: numericSellingPrice,
            reason: form.description || 'updated cost',
          }),
        });
        const data = response.data as { productType: ProductType };
        setItems((prev) =>
          prev.map((it) => (it.id === data.productType.id ? data.productType : it))
        );
        showSuccess('Prices updated');
      }
      setOpenDialog(false);
    } catch (error) {
      console.error('Error saving product type:', error);
      showError(
        error instanceof Error ? error.message : 'Failed to save product type'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleOpenHistory = async (item: ProductType) => {
    setOpenHistoryDialog(true);
    setHistoryItems([]);
    setHistoryForName(item.productType);
    setHistoryLoading(true);
    try {
      const response = await apiRequest(`/producttype/${item.id}`, {
        method: 'GET',
      });
      const data = (response.data || []) as CostHistoryItem[];
      setHistoryItems(
        data.map((h) => ({
          ...h,
          sellingPrice:
            h.sellingPrice != null && !Number.isNaN(Number(h.sellingPrice))
              ? Number(h.sellingPrice)
              : null,
        }))
      );
    } catch (error) {
      console.error('Error loading cost history:', error);
      showError(
        error instanceof Error ? error.message : 'Failed to load cost history'
      );
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleOpenDelete = (item: ProductType) => {
    setDeleteTarget(item);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setSaving(true);
      await apiRequest(`/producttype/${deleteTarget.id}`, { method: 'DELETE' });
      setItems((prev) => prev.filter((it) => it.id !== deleteTarget.id));
      showSuccess('Product type deleted');
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
    } catch (error) {
      console.error('Error deleting product type:', error);
      showError(
        error instanceof Error ? error.message : 'Failed to delete product type'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredItems = searchQuery.trim()
    ? items.filter((item) => {
        const q = searchQuery.toLowerCase();
        return (
          item.productType.toLowerCase().includes(q) ||
          (item.description ?? '').toLowerCase().includes(q) ||
          (item.unit ?? '').toLowerCase().includes(q)
        );
      })
    : items;

  const displayedItems = filteredItems.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: isMobile ? 2 : 0 }}>
          <Box>
            <Typography variant="overline" color="text.secondary">
              Settings
            </Typography>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
              Product Types
            </Typography>
          </Box>
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                size="small"
                placeholder="Search…"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 220 }}
              />
              <IconButton onClick={loadItems} title="Refresh">
                <RefreshIcon />
              </IconButton>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenCreate}
                sx={{ bgcolor: 'grey.900', '&:hover': { bgcolor: 'grey.800' } }}
              >
                New Product Type
              </Button>
            </Box>
          )}
        </Box>
        {isMobile && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search…"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1, minWidth: 0 }}
            />
            <IconButton onClick={loadItems} title="Refresh">
              <RefreshIcon />
            </IconButton>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
              sx={{ bgcolor: 'grey.900', '&:hover': { bgcolor: 'grey.800' }, whiteSpace: 'nowrap' }}
            >
              New
            </Button>
          </Box>
        )}
      </Box>

      {loading ? (
        <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : isMobile ? (
        /* Mobile card layout */
        <>
          {displayedItems.length === 0 ? (
            <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
              {searchQuery.trim() ? 'No results match your search' : 'No product types found'}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {displayedItems.map((item, index) => (
                <Card key={item.id} variant="outlined">
                  <CardContent sx={{ pb: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        #{index + 1 + page * rowsPerPage}
                      </Typography>
                      {item.unit && (
                        <Chip label={item.unit} size="small" variant="outlined" />
                      )}
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {item.productType}
                    </Typography>
                    {item.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {item.description}
                      </Typography>
                    )}
                    <Typography variant="h6" sx={{ mt: 1, color: 'success.dark', fontWeight: 700 }}>
                      {item.sellingPrice != null ? `$${item.sellingPrice.toFixed(2)}` : '-'}
                    </Typography>
                    {item.updatedAt && (
                      <Typography variant="caption" color="text.secondary">
                        Updated {new Date(item.updatedAt).toLocaleDateString()}
                      </Typography>
                    )}
                  </CardContent>
                  <CardActions sx={{ pt: 0, justifyContent: 'flex-end' }}>
                    <Tooltip title="Edit cost">
                      <IconButton size="small" onClick={() => handleOpenEditCost(item)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="View cost history">
                      <IconButton size="small" onClick={() => handleOpenHistory(item)}>
                        <HistoryIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete product type">
                      <IconButton size="small" onClick={() => handleOpenDelete(item)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </Card>
              ))}
            </Box>
          )}
          <TablePagination
            component="div"
            count={filteredItems.length}
            rowsPerPageOptions={[5, 10, 25]}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ mt: 1 }}
          />
        </>
      ) : (
        /* Desktop table layout */
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Product Type</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Selling Price</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Updated At</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      {searchQuery.trim() ? 'No results match your search' : 'No product types found'}
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedItems.map((item, index) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{index + 1 + page * rowsPerPage}</TableCell>
                      <TableCell>{item.productType}</TableCell>
                      <TableCell>{item.description || '-'}</TableCell>
                      <TableCell>
                        {item.sellingPrice != null
                          ? `$${item.sellingPrice.toFixed(2)}`
                          : '-'}
                      </TableCell>
                      <TableCell>{item.unit || '-'}</TableCell>
                      <TableCell>
                        {item.createdAt
                          ? new Date(item.createdAt).toLocaleString()
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {item.updatedAt
                          ? new Date(item.updatedAt).toLocaleString()
                          : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit cost">
                          <IconButton onClick={() => handleOpenEditCost(item)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View cost history">
                          <IconButton onClick={() => handleOpenHistory(item)}>
                            <HistoryIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete product type">
                          <IconButton
                            onClick={() => handleOpenDelete(item)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredItems.length}
            rowsPerPageOptions={[5, 10, 25, 50]}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>{isEdit ? 'Update Prices' : 'New Product Type'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Product Type"
              value={form.productType}
              onChange={(e) => setForm({ ...form, productType: e.target.value })}
              fullWidth
              required
              disabled={isEdit}
            />
            <TextField
              label={isEdit ? 'Reason for cost change' : 'Description'}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label="Selling Price"
              value={form.sellingPrice}
              onChange={(e) =>
                setForm({ ...form, sellingPrice: e.target.value })
              }
              fullWidth
              required
              type="number"
              inputProps={{ step: '0.01' }}
            />
            <TextField
              select
              label="Unit"
              value={form.unit}
              onChange={(e) => setForm({ ...form, unit: e.target.value })}
              fullWidth
            >
              <MenuItem value="Sq ft">Sq ft</MenuItem>
              <MenuItem value="Ln ft">Ln ft</MenuItem>
              <MenuItem value="Hardware">Hardware</MenuItem>
              <MenuItem value="item">item</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={
              saving || !form.productType || !form.sellingPrice
            }
          >
            {saving ? <CircularProgress size={24} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Product Type</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{' '}
            <strong>{deleteTarget?.productType}</strong>? This will also remove
            all its cost history. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={saving}
          >
            {saving ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Delete'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* History dialog */}
      <Dialog
        open={openHistoryDialog}
        onClose={() => setOpenHistoryDialog(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Cost History - {historyForName}</DialogTitle>
        <DialogContent>
          {historyLoading ? (
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : historyItems.length === 0 ? (
            <Typography sx={{ p: 2 }}>No history found.</Typography>
          ) : (
            <>
              {/* Line chart for prices over time */}
              <Box sx={{ width: '100%', height: 240, mb: 3 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={[...historyItems]
                      .slice()
                      .reverse()
                      .map((h) => ({
                        label: h.createdAt
                          ? new Date(h.createdAt).toLocaleTimeString()
                          : '',
                        sellingPrice: h.sellingPrice,
                      }))}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" />
                    <YAxis />
                    <RechartsTooltip
                      formatter={(value: any, name: string | undefined) => [
                        `$${Number(value).toFixed(2)}`,
                        name,
                      ]}
                    />
                    <Line
                      type="monotone"
                      dataKey="sellingPrice"
                      name="Selling Price"
                      stroke="#2e7d32"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>

              {/* History table */}
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Selling Price</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Registered By</TableCell>
                    <TableCell>Created At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyItems.map((h, index) => (
                    <TableRow key={h.id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        {h.sellingPrice != null
                          ? `$${h.sellingPrice.toFixed(2)}`
                          : '-'}
                      </TableCell>
                      <TableCell>{h.reason || '-'}</TableCell>
                      <TableCell>{h.userName || '-'}</TableCell>
                      <TableCell>
                        {h.createdAt
                          ? new Date(h.createdAt).toLocaleString()
                          : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenHistoryDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

